import React, { Suspense, useState, useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Preload, useGLTF } from "@react-three/drei";
import styled from "styled-components";
import CanvasErrorBoundary from "./CanvasErrorBoundary";

// Define a styled container to maintain responsive sizing even when unmounted.
const CanvasContainer = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  @media (max-width: 960px) {
    height: 200px;
  }
  @media (max-width: 640px) {
    height: 180px;
  }
`;

const Earth = () => {
  const earth = useGLTF("./planet/scene.gltf");
  return (
    <primitive object={earth.scene} scale={2.2} position-y={0} rotation-y={0} />
  );
};

const EarthCanvas = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        rootMargin: "100px", // Start loading the model slightly before it scrolls into view
        threshold: 0.01,
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <CanvasContainer ref={containerRef}>
      {isVisible && (
        <CanvasErrorBoundary>
          <Canvas
            shadows
            frameloop="demand"
            dpr={[1, 2.5]}
            camera={{
              fov: 45,
              near: 0.1,
              far: 200,
              position: [-4, 3, 6],
            }}
          >
            <Suspense fallback={null}>
              <OrbitControls
                autoRotate
                enableZoom={false}
                maxPolarAngle={Math.PI / 2}
                minPolarAngle={Math.PI / 2}
              />
              <Earth />
              <Preload all />
            </Suspense>
          </Canvas>
        </CanvasErrorBoundary>
      )}
    </CanvasContainer>
  );
};

export default EarthCanvas;
